import React, { useEffect, useRef } from 'react';
import { ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface ErrorBannerProps {
  error: string | null;
  onClose?: () => void;
  autoScroll?: boolean;
  className?: string;
  type?: 'error' | 'warning' | 'info';
}

export const ErrorBanner: React.FC<ErrorBannerProps> = ({
  error,
  onClose,
  autoScroll = true,
  className = '',
  type = 'error',
}) => {
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (error && autoScroll && bannerRef.current) {
      // Small delay to ensure the banner is rendered and positioned
      const scrollTimeout = setTimeout(() => {
        if (bannerRef.current) {
          const elementRect = bannerRef.current.getBoundingClientRect();
          const absoluteElementTop = elementRect.top + window.pageYOffset;

          // Account for fixed navbar/header height (adjust as needed for your layout)
          const headerOffset = 80; // Adjust this value based on your header height
          const offsetPosition = absoluteElementTop - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      }, 100);

      return () => clearTimeout(scrollTimeout);
    }
  }, [error, autoScroll]);

  if (!error) return null;

  const getColorClasses = () => {
    switch (type) {
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'info':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      default:
        return 'bg-red-50 border-red-200 text-red-800';
    }
  };

  const getIconColorClass = () => {
    switch (type) {
      case 'error':
        return 'text-red-400';
      case 'warning':
        return 'text-yellow-400';
      case 'info':
        return 'text-blue-400';
      default:
        return 'text-red-400';
    }
  };

  const getMessageColorClass = () => {
    switch (type) {
      case 'error':
        return 'text-red-700';
      case 'warning':
        return 'text-yellow-700';
      case 'info':
        return 'text-blue-700';
      default:
        return 'text-red-700';
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'error':
        return 'Error';
      case 'warning':
        return 'Warning';
      case 'info':
        return 'Information';
      default:
        return 'Error';
    }
  };

  return (
    <div
      ref={bannerRef}
      className={`border rounded-lg p-4 mb-4 ${getColorClasses()} ${className}`}
      role="alert"
    >
      <div className="flex">
        <ExclamationCircleIcon className={`h-5 w-5 ${getIconColorClass()}`} />
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium">{getTitle()}</h3>
          <div className={`mt-2 text-sm ${getMessageColorClass()}`}>
            <p>{error}</p>
          </div>
        </div>
        {onClose && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                onClick={onClose}
                className={`inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  type === 'error'
                    ? 'text-red-500 hover:bg-red-100 focus:ring-red-600'
                    : type === 'warning'
                      ? 'text-yellow-500 hover:bg-yellow-100 focus:ring-yellow-600'
                      : 'text-blue-500 hover:bg-blue-100 focus:ring-blue-600'
                }`}
              >
                <span className="sr-only">Dismiss</span>
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
